import { v7 as uuid } from 'uuid';
import { EMBED_AGENT_WIDGET_JS_FILENAME } from '@/constants/files';
import { EMBED_AGENT_WIDGET_RUNTIME_SCRIPT_ATTR } from '@/constants/widget';
import { type RfEmbedSdkConfig, type RfEmbedSdkConfigInput, type RfEmbedSdkConfigLegacy, type RfEmbedSdkSearchConfig, type RfEmbedSdkWidgetRef, type DeferredRfEmbedSdkWidgetRef, type RfEmbedSdkQueueEntry, RfEmbedSdkDisplayType } from '@repo/shared/types/core/widget';
import { validateRfEmbedSdkConfig } from '@repo/shared/utils/core/widget';
import { resolveRfEmbedSdkConfig, isRFWidgetLegacyConfig, translateRFWidgetLegacyConfig } from '@/utils/legacy';

(() => {
	try {
		if (typeof document === 'undefined' || typeof window === 'undefined' || !(document.head instanceof HTMLHeadElement)) {
			return;
		}
		const RUNTIME_SCRIPT_ATTR = EMBED_AGENT_WIDGET_RUNTIME_SCRIPT_ATTR;
		const RUNTIME_FILENAME = EMBED_AGENT_WIDGET_JS_FILENAME;
		if (document.querySelector(`script[${RUNTIME_SCRIPT_ATTR}]`)) {
			return;
		}
		let queue: RfEmbedSdkQueueEntry[] = (Array.isArray(window.__rapidflareQueue) && window.__rapidflareQueue?.length > 0) ? window.__rapidflareQueue : new Array<RfEmbedSdkQueueEntry>();
		if (!Array.isArray(window.__rapidflareQueue)) {
			window.__rapidflareQueue = queue;
		} else {
			queue = window.__rapidflareQueue;
		}
		window.rapidflare = {
			getInstances: (): RfEmbedSdkWidgetRef[] => new Array<RfEmbedSdkWidgetRef>(),
			init: (rawConfig: RfEmbedSdkConfigInput): RfEmbedSdkWidgetRef => {
				const config = resolveRfEmbedSdkConfig(rawConfig);
				if (config === null || !validateRfEmbedSdkConfig(config)) {
					const noopRef: RfEmbedSdkWidgetRef = {
						id: uuid(),
						open() { /* noop */ },
						close() { /* noop */ },
						maximize() { /* noop */ },
						minimize() { /* noop */ },
						update() { /* noop */ },
						resetConversation() { /* noop */ },
						destroy() { /* noop */ },
						config() {
							return rawConfig;
						}
					};
					return noopRef;
				}
				const entry: RfEmbedSdkQueueEntry = { config: { ...config }, ref: null };
				const ref: DeferredRfEmbedSdkWidgetRef = {
					// Honor a customer-supplied top-level `id` on the deferred stub so `ref.id` is correct even
					// before the runtime queue drain reconciles it; falls back to a uuid otherwise.
					id: (typeof config?.id === 'string' && config?.id?.length > 0) ? config?.id : uuid(),
					open() {
						if (!this._resolved) {
							entry.pendingOpen = true;
						}
					},
					close() {
						if (!this._resolved) {
							entry.pendingOpen = false;
						}
					},
					maximize() {
						if (!this._resolved) {
							entry.pendingMaximized = true;
						}
					},
					minimize() {
						if (!this._resolved) {
							entry.pendingMaximized = false;
						}
					},
					update(partial: Partial<RfEmbedSdkConfig>) {
						if (!this._resolved && entry?.config) {
							const merged = { ...entry?.config, ...partial };
							// Merge `display` rather than replacing it, as the runtime does: a partial
							// like `{ display: { productCompareUrl } }` would otherwise drop `type` and
							// fail validation, silently discarding the update.
							if (typeof partial?.display === 'object' && partial?.display !== null) {
								merged.display = { ...entry?.config?.display, ...partial?.display };
							}
							// The instance id and a search widget's `action` are immutable via update()
							// (mirrors the runtime guards; the runtime logs an error for these attempts).
							merged.id = entry?.config?.id;
							if (entry?.config?.display?.type === RfEmbedSdkDisplayType.SEARCH && typeof merged?.display === 'object' && merged?.display !== null && merged?.display?.type === RfEmbedSdkDisplayType.SEARCH) {
								merged.display = { ...merged.display, action: (entry?.config?.display as RfEmbedSdkSearchConfig)?.action };
							}
							if (validateRfEmbedSdkConfig(merged, entry?.config?.display?.type)) {
								entry.config = merged;
							}
						}
					},
					resetConversation() {
						// A widget queued before the runtime loaded has no conversation yet, so there is
						// nothing to reset; the runtime rebinds this to the real implementation on drain.
					},
					destroy() {
						if (this._resolved) {
							this._element?.remove?.();
							return;
						}
						const pendingQueue = window.__rapidflareQueue;
						if (Array.isArray(pendingQueue)) {
							const entryIndex = pendingQueue?.indexOf?.(entry);
							if (entryIndex > -1) {
								pendingQueue?.splice?.(entryIndex, 1);
							}
						}
					},
					config() {
						return { ...entry?.config };
					}
				};
				ref._resolved = false;
				ref._entry = entry;
				entry.ref = ref;
				queue?.push?.(entry);
				return ref;
			}
		};
		if (isRFWidgetLegacyConfig(window.RFCopilotConfig)) {
			window.rapidflare?.init(translateRFWidgetLegacyConfig(window.RFCopilotConfig));
		}
		window.RFCopilot = (config: RfEmbedSdkConfigLegacy) => window.rapidflare?.init(translateRFWidgetLegacyConfig(config));
		const currentScript = document.currentScript as HTMLScriptElement | null;
		const baseUrl = typeof currentScript?.src === 'string' && currentScript?.src?.length > 0 ? (new URL('.', currentScript?.src))?.toString?.() : '';
		const runtimeSrc = typeof baseUrl === 'string' && baseUrl?.length > 0 ? `${baseUrl}${RUNTIME_FILENAME}` : RUNTIME_FILENAME;
		const script = document.createElement<'script'>('script');
		script.src = runtimeSrc;
		script.type = 'module';
		script.async = true;
		script.setAttribute(RUNTIME_SCRIPT_ATTR, 'true');
		document.head.appendChild(script);
	} catch {
		// Do nothing
	}
})();
